import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getFavorites, removeFavorite } from '../services/api';
import { toast } from 'sonner';
import { Heart, Play, Tv2, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';

export default function FavoritesPage() {
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchFavorites = async () => {
      try {
        const data = await getFavorites();
        setFavorites(data);
      } catch (error) {
        toast.error('Failed to load favorites');
      } finally {
        setLoading(false);
      }
    };
    fetchFavorites();
  }, []);

  const handleRemove = async (channelId, e) => {
    e.stopPropagation();
    try {
      await removeFavorite(channelId);
      setFavorites(prev => prev.filter(f => f.id !== channelId));
      toast.success('Removed from favorites');
    } catch (error) {
      toast.error('Failed to remove from favorites');
    }
  };

  const handleWatch = (channelId) => {
    navigate(`/watch/${channelId}`);
  };

  if (loading) {
    return (
      <div className="space-y-6" data-testid="favorites-loading">
        <h1 className="text-3xl font-bold">Favorites</h1>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="aspect-video rounded-xl skeleton" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="favorites-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Favorites</h1>
          <p className="text-white/60 mt-1">{favorites.length} channels</p>
        </div>
      </div>

      {favorites.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <div className="w-20 h-20 rounded-full bg-[rgb(31,31,34)] flex items-center justify-center mb-6">
            <Heart className="w-10 h-10 text-white/30" />
          </div>
          <h3 className="text-xl font-semibold mb-2">No Favorites Yet</h3>
          <p className="text-white/50 max-w-md mb-6">
            Add channels to your favorites for quick access
          </p>
          <Button onClick={() => navigate('/')} className="rounded-full bg-[rgb(124,58,237)]">
            Browse Channels
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
          {favorites.map((channel) => (
            <div
              key={channel.id}
              className="channel-card group relative rounded-xl overflow-hidden bg-[rgb(15,15,16)] border border-white/5 cursor-pointer"
              onClick={() => handleWatch(channel.id)}
              data-testid={`favorite-${channel.id}`}
            >
              <div className="aspect-video relative overflow-hidden">
                {channel.logo ? (
                  <img
                    src={channel.logo}
                    alt={channel.name}
                    className="channel-image w-full h-full object-cover"
                    onError={(e) => e.target.style.display = 'none'}
                  />
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-[rgb(124,58,237)]/20 to-[rgb(16,185,129)]/20 flex items-center justify-center">
                    <Tv2 className="w-12 h-12 text-white/20" />
                  </div>
                )}
                <div className="gradient-overlay absolute inset-0" />
                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                  <div className="w-14 h-14 rounded-full bg-[rgb(124,58,237)] flex items-center justify-center glow-primary">
                    <Play className="w-6 h-6 text-white ml-1" />
                  </div>
                </div>
                <button
                  onClick={(e) => handleRemove(channel.id, e)}
                  className="absolute top-3 right-3 w-8 h-8 rounded-full bg-red-500/80 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
                  data-testid={`remove-favorite-${channel.id}`}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
              <div className="p-3">
                <h4 className="font-medium truncate">{channel.name}</h4>
                <p className="text-xs text-white/40 truncate">{channel.group}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
